import { fetchWithFallback } from '@/config/api';

interface PreloadData {
  orders: any[];
  appointments: any[];
  notifications: any[];
  profile: any;
  messages: any[];
  tailors: any[];
  measurements: any[];
}

class DataPreloader {
  private cache: Map<string, any> = new Map();
  private preloadPromises: Map<string, Promise<any>> = new Map();

  // Start preloading data based on email (before password is entered)
  async startPreloading(email: string): Promise<void> {
    try {
      // Check if user exists and get basic info
      const userCheckResponse = await fetchWithFallback('/api/users/check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (userCheckResponse.ok) {
        const userData = await userCheckResponse.json();
        
        // Start preloading data in parallel
        const preloadPromises = [
          this.preloadOrders(userData.id),
          this.preloadAppointments(userData.id),
          this.preloadNotifications(userData.id),
          this.preloadProfile(userData.id),
          this.preloadMessages(userData.id),
          this.preloadTailors(userData.id),
          this.preloadMeasurements(userData.id),
        ];

        // Don't wait for completion - let them run in background
        Promise.allSettled(preloadPromises);
      }
    } catch (_error) {
      console.log('Preloading started but will continue in background:', error);
    }
  }

  private async preloadOrders(userId: string): Promise<any[]> {
    const cacheKey = `orders_${userId}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.preloadPromises.has(cacheKey)) {
      return this.preloadPromises.get(cacheKey);
    }

    // Check if we have a valid token before making the request
    const token = localStorage.getItem('authToken');
    if (!token) {
      // Debug level - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.debug('No auth token available for orders preload');
      }
      return [];
    }

    const promise = fetchWithFallback('/api/orders', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        this.cache.set(cacheKey, data.orders || []);
        return data.orders || [];
      })
      .catch(error => {
        console.log('Orders preload failed:', error);
        return [];
      });

    this.preloadPromises.set(cacheKey, promise);
    return promise;
  }

  private async preloadAppointments(userId: string): Promise<any[]> {
    const cacheKey = `appointments_${userId}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.preloadPromises.has(cacheKey)) {
      return this.preloadPromises.get(cacheKey);
    }

    // Check if we have a valid token before making the request
    const token = localStorage.getItem('authToken');
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('No auth token available for appointments preload');
      }
      return [];
    }

    const promise = fetchWithFallback('/api/appointments', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        this.cache.set(cacheKey, data.appointments || []);
        return data.appointments || [];
      })
      .catch(error => {
        console.log('Appointments preload failed:', error);
        return [];
      });

    this.preloadPromises.set(cacheKey, promise);
    return promise;
  }

  private async preloadNotifications(userId: string): Promise<any[]> {
    const cacheKey = `notifications_${userId}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.preloadPromises.has(cacheKey)) {
      return this.preloadPromises.get(cacheKey);
    }

    // Check if we have a valid token before making the request
    const token = localStorage.getItem('authToken');
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('No auth token available for notifications preload');
      }
      return [];
    }

    const promise = fetchWithFallback('/api/notifications', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        this.cache.set(cacheKey, data.notifications || []);
        return data.notifications || [];
      })
      .catch(error => {
        console.log('Notifications preload failed:', error);
        return [];
      });

    this.preloadPromises.set(cacheKey, promise);
    return promise;
  }

  private async preloadProfile(userId: string): Promise<any> {
    const cacheKey = `profile_${userId}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.preloadPromises.has(cacheKey)) {
      return this.preloadPromises.get(cacheKey);
    }

    // Check if we have a valid token before making the request
    const token = localStorage.getItem('authToken');
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('No auth token available for profile preload');
      }
      return null;
    }

    const promise = fetchWithFallback('/api/customer/profile', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        this.cache.set(cacheKey, data);
        return data;
      })
      .catch(error => {
        console.log('Profile preload failed:', error);
        return null;
      });

    this.preloadPromises.set(cacheKey, promise);
    return promise;
  }

  private async preloadMessages(userId: string): Promise<any[]> {
    const cacheKey = `messages_${userId}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.preloadPromises.has(cacheKey)) {
      return this.preloadPromises.get(cacheKey);
    }

    // Check if we have a valid token before making the request
    const token = localStorage.getItem('authToken');
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('No auth token available for messages preload');
      }
      return [];
    }

    const promise = fetchWithFallback('/api/chat/conversations', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        this.cache.set(cacheKey, data.conversations || []);
        return data.conversations || [];
      })
      .catch(error => {
        console.log('Messages preload failed:', error);
        return [];
      });

    this.preloadPromises.set(cacheKey, promise);
    return promise;
  }

  private async preloadTailors(userId: string): Promise<any[]> {
    const cacheKey = `tailors_${userId}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.preloadPromises.has(cacheKey)) {
      return this.preloadPromises.get(cacheKey);
    }

    // Check if we have a valid token before making the request
    const token = localStorage.getItem('authToken');
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('No auth token available for tailors preload');
      }
      return [];
    }

    const promise = fetchWithFallback('/api/tailors', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        this.cache.set(cacheKey, data.tailors || []);
        return data.tailors || [];
      })
      .catch(error => {
        console.log('Tailors preload failed:', error);
        return [];
      });

    this.preloadPromises.set(cacheKey, promise);
    return promise;
  }

  private async preloadMeasurements(userId: string): Promise<any[]> {
    const cacheKey = `measurements_${userId}`;
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.preloadPromises.has(cacheKey)) {
      return this.preloadPromises.get(cacheKey);
    }

    // Check if we have a valid token before making the request
    const token = localStorage.getItem('authToken');
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('No auth token available for measurements preload');
      }
      return [];
    }

    const promise = fetchWithFallback('/api/measurements', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        this.cache.set(cacheKey, data.measurements || []);
        return data.measurements || [];
      })
      .catch(error => {
        console.log('Measurements preload failed:', error);
        return [];
      });

    this.preloadPromises.set(cacheKey, promise);
    return promise;
  }

  // Get preloaded data
  getPreloadedData(userId: string, dataType: keyof PreloadData): any {
    const cacheKey = `${dataType}_${userId}`;
    return this.cache.get(cacheKey);
  }

  // Check if data is preloaded
  isPreloaded(userId: string, dataType: keyof PreloadData): boolean {
    const cacheKey = `${dataType}_${userId}`;
    return this.cache.has(cacheKey);
  }

  // Clear cache for user
  clearUserCache(userId: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => key.includes(userId));
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  // Clear all cache
  clearAllCache(): void {
    this.cache.clear();
    this.preloadPromises.clear();
  }
}

export const dataPreloader = new DataPreloader();
